// Employee Analytics section — FE-032 / spec §7.0.2.
//
// Pulls dashboard filters from the shared provider, calls
// useEmployeeAnalyticsQuery, and renders the FE-032 widget set
// (forecast cards + workload + blocked + delivery + days-to-complete
// trend). Loading / error / empty states use the FE-031 primitives so
// the section behaves consistently with FE-033..FE-037.

import {
  DashboardCard,
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
  useCommonFilters,
  useDashboardDateRange,
  useEmployeeAnalyticsQuery,
  type EmployeeAnalyticsResponse,
} from "@/features/dashboard";

import { BlockedWorkBar } from "./BlockedWorkBar";
import { DaysToCompleteTrend } from "./DaysToCompleteTrend";
import { DeliveryBar } from "./DeliveryBar";
import { ForecastCards } from "./ForecastCards";
import { WorkloadBar } from "./WorkloadBar";


export const EMPLOYEE_ANALYTICS_SECTION = "employee-analytics" as const;
export const EMPLOYEE_ANALYTICS_SPAN_CLASS =
  "xl:col-span-2 md:col-span-2";


export function EmployeeAnalyticsSection() {
  const filters = useCommonFilters();
  const dateRange = useDashboardDateRange();
  const query = useEmployeeAnalyticsQuery(filters);

  return (
    <DashboardCard
      section={EMPLOYEE_ANALYTICS_SECTION}
      className={EMPLOYEE_ANALYTICS_SPAN_CLASS}
      title="Employee Analytics"
      subtitle="Capacity, blocking work, delivery, and days-to-complete trend"
    >
      {query.isLoading ? (
        <DashboardLoadingState variant="card" />
      ) : query.isError ? (
        <DashboardErrorState
          title="Couldn't load Employee Analytics"
          description="Refresh to retry, or check the network connection."
          retry={() => query.refetch()}
        />
      ) : query.data ? (
        isAllZero(query.data) ? (
          <DashboardEmptyState
            title="No Employee Analytics data yet"
            description="Once Commitments are assigned and completed, capacity and delivery metrics appear here."
          />
        ) : (
          <div className="flex flex-col gap-6">
            <ForecastCards
              forecast={query.data.forecast}
              dateRange={dateRange}
            />
            {/* Each chart owns a full row now (was a 2-col grid).
                Workload / Blocked / Delivery each paginate through
                their own list of employees so a brokerage with 30+
                staff doesn't see every name squashed into one
                viewport. */}
            <div className="flex flex-col gap-6">
              <WorkloadBar
                rows={query.data.workload_bar}
                dateRange={dateRange}
              />
              <BlockedWorkBar
                rows={query.data.blocking_bar}
                dateRange={dateRange}
              />
              <DeliveryBar
                rows={query.data.delivery_bar}
                dateRange={dateRange}
              />
              <DaysToCompleteTrend trend={query.data.days_to_complete_trend} />
            </div>
          </div>
        )
      ) : null}
    </DashboardCard>
  );
}


function isAllZero(data: EmployeeAnalyticsResponse): boolean {
  const fc = data.forecast;
  return (
    fc.at_risk === 0 &&
    fc.on_track === 0 &&
    fc.underassigned === 0 &&
    data.workload_bar.length === 0 &&
    data.blocking_bar.length === 0 &&
    data.delivery_bar.length === 0 &&
    data.days_to_complete_trend.length === 0
  );
}
